import { deactivatePlan, getPaginatedPlans } from '../../../api/planApi'
import YesOrNoTag from '../../../components/common/YesOrNoTag'
import LinkButton from '../../../components/link-button/link-button'
import PlanFilter from '../../../components/plans/plan-filter/plan-filter'
import { getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import {
  IPlan,
  IPlanFilterParams,
  PaginatedPlan,
  PaginatedPlanFilterParams,
  PlanStatusEnums,
  PlanTypeEnums,
} from '../share-type'
import styles from './plans.module.less'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Card, Col, Popconfirm, Row, Space, Table, Tag, notification } from 'antd'
import Text from 'antd/es/typography/Text'
import { ColumnsType } from 'antd/lib/table'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const getPlanStatusText = (status?: PlanStatusEnums) => {
  switch (status) {
    case PlanStatusEnums.ACTIVE:
      return translate('plan.status.active')
    case PlanStatusEnums.DELETED:
      return translate('plan.status.deleted')
    case PlanStatusEnums.ARCHIVED:
    default:
      return translate('plan.status.archived')
  }
}

const Plans = () => {
  const navigate = useNavigate()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<IPlanFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [plans, setPlans] = useState<IPlan[]>([])

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'name', isBoolean: false },
        { key: 'status', isBoolean: false },
        { key: 'planType', isBoolean: false },
        { key: 'isTopUp', isBoolean: true },
        { key: 'isNewUserOnly', isBoolean: true },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedPlansAction(data)
  }, [])

  const getPaginatedPlansAction = (data: PaginatedPlanFilterParams) => {
    getPaginatedPlans(data).then((res: PaginatedPlan) => {
      setPlans(res.data)
      setPageInfo({
        ...res.pageInfo,
        currentPage: res.pageInfo.currentPage + 1,
      })
    })
  }

  const onSearch = (values: IPlanFilterParams) => {
    const data = {
      page: 0,
      size: pageInfo.pageSize,
      ...values,
    }
    setSearchValues(values)
    setSearchParams(getSearchParams(values))
    getPaginatedPlansAction(data)
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    getPaginatedPlansAction({
      ...searchValues,
      size: pageSize,
      page: page - 1,
    })
  }

  const onDeactivate = (id: number) => {
    deactivatePlan(id).then(() => {
      notification.success({ title: translate('deactivate.success') })
      getPaginatedPlansAction({
        ...searchValues,
        size: pageInfo.pageSize,
        page: pageInfo.currentPage - 1,
      })
    })
  }

  const columns: ColumnsType<IPlan> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: translate('name'), dataIndex: 'name', key: 'name' },
    {
      title: translate('plan.type'),
      dataIndex: 'planType',
      key: 'planType',
      render: (planType: PlanTypeEnums) => translate(`plan.type.${planType}`),
    },
    {
      title: translate('plan.price'),
      dataIndex: 'price',
      key: 'price',
      render: (price?: number) => (price === undefined ? '' : `¥${price / 100}`),
    },
    { title: translate('plan.credits'), dataIndex: 'credits', key: 'credits' },
    {
      title: translate('plan.validityPeriod'),
      dataIndex: 'validityPeriod',
      key: 'validityPeriod',
      render: (validityPeriod?: number, record?: IPlan) =>
        record?.planType === PlanTypeEnums.SUBSCRIPTION
          ? `${record.period || 1} ${record.periodUnit || 'MONTH'}`
          : validityPeriod,
    },
    {
      title: translate('plan.miniProgramSelectable'),
      dataIndex: 'miniProgramSelectable',
      key: 'miniProgramSelectable',
      render: (value?: boolean) => <YesOrNoTag value={value || false} />,
    },
    {
      title: translate('plan.isTopUp'),
      dataIndex: 'isTopUp',
      key: 'isTopUp',
      render: (value: boolean) => <YesOrNoTag value={value} />,
    },
    {
      title: translate('plan.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status?: PlanStatusEnums) => (
        <Tag color={status === PlanStatusEnums.ACTIVE ? 'green' : 'default'}>
          {getPlanStatusText(status)}
        </Tag>
      ),
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (record: IPlan) => (
        <Space wrap>
          <AuthPermission permission={PermissionsEnum.PLANS_READ}>
            <LinkButton to={`/plans/${record.id}`}>{translate('view')}</LinkButton>
          </AuthPermission>
          <AuthPermission permission={PermissionsEnum.PLANS_WRITE}>
            <LinkButton to={`/plans/${record.id}/edit`}>{translate('edit')}</LinkButton>
          </AuthPermission>
          {record.status === PlanStatusEnums.ACTIVE && (
            <AuthPermission permission={PermissionsEnum.PLANS_WRITE}>
              <Popconfirm
                title={translate('plan.deactivateConfirm')}
                onConfirm={() => onDeactivate(record.id)}
                okText={translate('yes')}
                cancelText={translate('no')}
              >
                <Text type="danger" styles={{ root: { cursor: 'pointer' } }}>
                  {translate('plan.deactivate')}
                </Text>
              </Popconfirm>
            </AuthPermission>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Card>
      <Space className={styles['plan-list-space']} orientation="vertical" size={16}>
        <Row justify="center" align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['left-col']}>
            <div>{translate('navBar.plansTitle')}</div>
          </Col>
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['right-col']}>
            <AuthPermission permission={PermissionsEnum.PLANS_WRITE}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/plans/new')}>
                {translate('navBar.newPlan')}
              </Button>
            </AuthPermission>
          </Col>
        </Row>
        <div className={styles['filter-bar']}>
          <PlanFilter onSearch={onSearch} formData={searchValues} />
        </div>
        <Table
          rowKey={record => record.id}
          columns={columns}
          dataSource={plans}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageInfo.pageSize,
            total: pageInfo.count,
            onChange: paginationOnChange,
          }}
          size="medium"
        />
      </Space>
    </Card>
  )
}

export default Plans
